import React, { useEffect, useMemo, useState } from 'react';

import PrimaryButton, {
  PrimaryButtonColor,
} from 'components/common/buttons/decorators/PrimaryButton/PrimaryButton';
import { DocumentCard } from 'components/common/documents/DocumentCard';
import { ChevronIcon } from 'components/common/icons/ChevronIcon';
import { CrossIcon } from 'components/common/icons/CrossIcon';
import { ConfirmSmsCodeForm } from 'components/common/utils';
import { Loader } from 'components/common/utils/Loader';
import apiClientConfig from 'configs/app';
import smsConfig from 'configs/sms';
import {
  createAgentDocumentProvider,
  createProfileManager,
  createSignContractManager,
  createSmsFormStateStorage,
} from 'di';
import { formatDate } from 'formatters';
import { useEffectOnMount, useNavigateByName, useProfileData } from 'hooks';
import { useDispatch } from 'react-redux';
import { addAgentCode } from 'store/profile';
import { RouteDictionary } from 'utils';
import { ymAsync } from 'utils/functions/ymWrapper';

import Modal from '../Modal';
import styles from './SignContractModal.module.scss';

type Step = 'document' | 'loading' | 'sms';

const SMS_REQUEST_COOLDOWN_MS = 60_000;
const SMS_REQUEST_COOLDOWN_STORAGE_KEY = 'sign-contract-sms-requested-at';
const SMS_CONFIRMATION_UUID_STORAGE_KEY = 'sign-contract-confirmation-uuid';

const readLastSmsRequestedAt = (): number | null => {
  const raw = sessionStorage.getItem(SMS_REQUEST_COOLDOWN_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const formatCooldown = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const setSmsResendCooldown = (confirmationId: string): void => {
  const storage = createSmsFormStateStorage(`sign-contract-${confirmationId}`);
  const nextTryDate = new Date(Date.now() + SMS_REQUEST_COOLDOWN_MS);
  storage.set({
    attemptsLeft: smsConfig.smsMaxAttempts,
    nextTryDate,
  });
};

const resolveDocumentUrl = (uri: string): string =>
  uri.startsWith('http') ? uri : `${apiClientConfig.apiBaseUrl}${uri}`;

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

type Props = {
  onClose: () => void;
};

const SignContractModal = ({ onClose }: Props) => {
  const profileData = useProfileData();
  const navigate = useNavigateByName();
  const dispatch = useDispatch();
  const [step, setStep] = useState<Step>('document');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [documentUrl, setDocumentUrl] = useState('');
  const [documentUuid, setDocumentUuid] = useState('');
  const [confirmationUuid, setConfirmationUuid] = useState(() => {
    return sessionStorage.getItem(SMS_CONFIRMATION_UUID_STORAGE_KEY) ?? '';
  });
  const [lastSmsRequestedAt, setLastSmsRequestedAt] = useState<number | null>(
    readLastSmsRequestedAt
  );
  const [now, setNow] = useState(() => Date.now());

  const smsCooldownRemainingSec = useMemo(() => {
    if (!lastSmsRequestedAt) {
      return 0;
    }
    return Math.max(0, Math.ceil((lastSmsRequestedAt + SMS_REQUEST_COOLDOWN_MS - now) / 1000));
  }, [lastSmsRequestedAt, now]);

  const canRequestNewSms = smsCooldownRemainingSec === 0;

  useEffect(() => {
    if (canRequestNewSms) {
      return;
    }
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [canRequestNewSms]);

  const markSmsRequested = () => {
    const requestedAt = Date.now();
    setLastSmsRequestedAt(requestedAt);
    sessionStorage.setItem(SMS_REQUEST_COOLDOWN_STORAGE_KEY, String(requestedAt));
  };

  const clearSmsRequestCooldown = () => {
    setLastSmsRequestedAt(null);
    sessionStorage.removeItem(SMS_REQUEST_COOLDOWN_STORAGE_KEY);
    sessionStorage.removeItem(SMS_CONFIRMATION_UUID_STORAGE_KEY);
  };

  const persistConfirmationUuid = (uuid: string) => {
    setConfirmationUuid(uuid);
    sessionStorage.setItem(SMS_CONFIRMATION_UUID_STORAGE_KEY, uuid);
  };

  const fullDocumentUrl = useMemo(
    () => (documentUrl ? resolveDocumentUrl(documentUrl) : ''),
    [documentUrl]
  );

  const documentDate = useMemo(() => formatDate(new Date()), []);

  const isSmsStep = step === 'sms' && Boolean(confirmationUuid);
  const isRequestingSms = step === 'loading';
  const isDocumentPhase = !isSmsStep;
  const showDocumentLoader = loading || isRequestingSms;

  const showBack = isSmsStep;
  const showClose = !isRequestingSms;

  const goBack = () => {
    setStepError(null);
    if (isSmsStep) {
      setStep('document');
      return;
    }
    onClose();
  };

  useEffectOnMount(() => {
    if (!profileData.lenderVerified) {
      navigate(RouteDictionary.PROFILE);
    }
  });

  useEffectOnMount(() => {
    const loadDocument = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const provider = await createAgentDocumentProvider();
        const result = await provider.getDocumentUri();
        setDocumentUrl(result.agreementUri);
        setDocumentUuid(result.uuid);
      } catch (error) {
        setDocumentUrl('');
        setDocumentUuid('');
        setLoadError(getErrorMessage(error, 'Не удалось загрузить документ'));
      } finally {
        setLoading(false);
      }
    };

    void loadDocument();
  });

  const isSubmitDisabled = showDocumentLoader || !fullDocumentUrl || Boolean(loadError);

  const requestSmsCode = async () => {
    setStepError(null);

    if (confirmationUuid && !canRequestNewSms) {
      setStep('sms');
      return;
    }

    if (!canRequestNewSms) {
      setStepError(`Новый код можно отправить через ${formatCooldown(smsCooldownRemainingSec)}`);
      return;
    }

    setStep('loading');

    try {
      const manager = await createSignContractManager();
      const result = await manager.requestCode();
      persistConfirmationUuid(result.uuid);
      markSmsRequested();
      setSmsResendCooldown(result.uuid);
      setStep('sms');
      ymAsync('sign-agency-offer-button-get-code');
    } catch (error) {
      setStepError(getErrorMessage(error, 'Не удалось отправить код подтверждения'));
      setStep('document');
    }
  };

  return (
    <Modal active onClose={onClose} className={styles.modal}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {showBack ? (
            <button type="button" className={styles.backButton} onClick={goBack} aria-label="Назад">
              <ChevronIcon direction="left" color="var(--white-color)" />
            </button>
          ) : (
            <div className={styles.headerLeftSpacer} />
          )}
        </div>
        <div className={styles.headerTitle}>Агентский договор</div>
        <div className={styles.headerRight}>
          {showClose ? (
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Закрыть"
            >
              <CrossIcon preset="default" />
            </button>
          ) : (
            <div className={styles.headerRightSpacer} />
          )}
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.stepShell}>
          <div className={styles.stepMain}>
            {isDocumentPhase ? (
              <div className={styles.confirmStep}>
                <p className={styles.confirmDescription}>
                  Ознакомьтесь и примите условия агентского договора
                </p>
                <div className={styles.documentArea}>
                  {showDocumentLoader ? (
                    <Loader size="small" overlay={false} />
                  ) : loadError ? (
                    <div className={styles.hintError}>{loadError}</div>
                  ) : fullDocumentUrl ? (
                    <DocumentCard
                      className={styles.documentAreaCard}
                      title="Агентский договор"
                      documentUrl={fullDocumentUrl}
                      date={documentDate}
                    />
                  ) : null}
                </div>
              </div>
            ) : (
              <>
                {stepError ? <div className={styles.hintError}>{stepError}</div> : null}
                <ConfirmSmsCodeForm
                  variant="comfort"
                  compactLoading
                  title="Введите код"
                  prefix={`sign-contract-${confirmationUuid}`}
                  onCancel={() => {
                    setStepError(null);
                    setStep('document');
                  }}
                  sendCallback={async (smsCode) => {
                    const profileManager = await createProfileManager();
                    const agentCode = await profileManager.signContract();
                    dispatch(addAgentCode(agentCode));
                    const signManager = await createSignContractManager();
                    await signManager.confirmCode(confirmationUuid, smsCode, documentUuid);
                    ymAsync('sign-agency-offer-code-input');
                  }}
                  resendCallback={async () => {
                    if (!canRequestNewSms) {
                      throw new Error(
                        `Новый код можно отправить через ${formatCooldown(smsCooldownRemainingSec)}`
                      );
                    }
                    const manager = await createSignContractManager();
                    const result = await manager.requestCode();
                    persistConfirmationUuid(result.uuid);
                    markSmsRequested();
                    setSmsResendCooldown(result.uuid);
                  }}
                  successCallback={async () => {
                    clearSmsRequestCooldown();
                    ymAsync('sign-agency-offer-code-button');
                    onClose();
                  }}
                  onError={async (error) => {
                    setStepError(error.message);
                  }}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {isDocumentPhase ? (
        <div className={styles.footer}>
          {stepError ? <div className={styles.footerError}>{stepError}</div> : null}
          {!canRequestNewSms && confirmationUuid ? (
            <p className={styles.mutedLabel}>
              Новый код можно отправить через {formatCooldown(smsCooldownRemainingSec)}
            </p>
          ) : null}
          <PrimaryButton
            expanded
            large
            color={isSubmitDisabled ? PrimaryButtonColor.GRAY : PrimaryButtonColor.GREEN}
          >
            <button
              type="button"
              className={styles.submitButton}
              disabled={isSubmitDisabled}
              onClick={() => void requestSmsCode()}
            >
              {confirmationUuid && !canRequestNewSms
                ? 'Продолжить ввод кода'
                : 'Заключить агентский договор'}
            </button>
          </PrimaryButton>
        </div>
      ) : null}
    </Modal>
  );
};

export default SignContractModal;
